import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Spin,
  Select,
  Switch,
  Tabs,
} from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import useLatestRequest from "../utils/useLatestRequest";

const { Option } = Select;

const formatAmount = (v) =>
  typeof v === "number"
    ? new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(v)
    : v ?? "-";

const formatGstRate = (v, record) => {
  const rate = v ?? record?.gstPercentage;
  if (rate == null || rate === "") return "-";
  if (typeof rate === "string" && rate.includes("%")) return rate;
  return `${rate} %`;
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const parseMonthFromSheetLabel = (sheetName) => {
  if (!sheetName || typeof sheetName !== "string") return null;
  const token = sheetName.trim().split(/\s+/)[0];
  const idx = MONTH_NAMES.findIndex(
    (m) => m.toLowerCase() === token.toLowerCase()
  );
  return idx >= 0 ? idx + 1 : null;
};

const CreditNoteRegisterReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [allMonths, setAllMonths] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState(null);
  const [reportData, setReportData] = useState(null);

  const apiMonthFilter = useMemo(() => {
    if (allMonths) return null;
    return parseMonthFromSheetLabel(activeTabKey) ?? 4;
  }, [allMonths, activeTabKey]);

  const beginRequest = useLatestRequest();

  const fetchData = async (isExcel = false) => {
    const isCurrent = isExcel ? () => true : beginRequest();
    if (isExcel) setDownloading(true);
    else {
      setLoading(true);
      setReportData(null);
    }
    try {
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      if (allMonths) {
        params.set("allMonths", "true");
      } else if (apiMonthFilter != null) {
        params.set("month", String(apiMonthFilter));
      }
      if (isExcel) params.set("isExcel", "true");
      const url = `${apiBaseUrl}/inventoryManagement/getCreditNoteRegister?${params.toString()}`;

      const res = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (isExcel) {
        if (!res.ok) {
          message.error("Failed to download Excel");
          return;
        }
        const blob = await res.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = allMonths
          ? `Credit_Note_Register_${financialYear}_all_months.xlsx`
          : `Credit_Note_Register_${financialYear}_month_${apiMonthFilter ?? 4}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        return;
      }

      const j = await res.json();
      if (!isCurrent()) return;
      if (j.success) {
        const sheetNamesInOrder = j.sheetNamesInOrder || [];
        setReportData({
          financialYear: j.financialYear,
          startDate: j.startDate,
          endDate: j.endDate,
          dataByMonth: j.dataByMonth || {},
          sheetNamesInOrder,
        });
        if (sheetNamesInOrder.length > 0) {
          setActiveTabKey((prev) => {
            if (prev && sheetNamesInOrder.includes(prev)) return prev;
            return sheetNamesInOrder[0];
          });
        }
      } else {
        console.error("getCreditNoteRegister failed:", j.message);
        message.error(j.message || "Failed to fetch credit note register");
      }
    } catch (err) {
      console.error(err);
      message.error(
        isExcel ? "Error downloading Excel" : "Error fetching report"
      );
    } finally {
      if (isCurrent()) {
        setLoading(false);
        setDownloading(false);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [financialYear, allMonths, apiMonthFilter]);

  const handleDownloadExcel = () => {
    fetchData(true);
  };

  const columns = [
    {
      title: "CN No.",
      dataIndex: "cnNumber",
      key: "cnNumber",
      width: 190,
      align: "center",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 110,
      align: "center",
      render: (v) => v || "-",
    },
    {
      title: "Party Name",
      dataIndex: "partyName",
      key: "partyName",
      width: 220,
      align: "left",
    },
    {
      title: "GST No.",
      dataIndex: "gstNumber",
      key: "gstNumber",
      width: 150,
      align: "center",
      render: (v) => v || "-",
    },
    {
      title: "HSN",
      dataIndex: "HSN",
      key: "HSN",
      width: 100,
      align: "center",
      render: (v, record) => v ?? record?.hsn ?? "-",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: "right",
      render: formatAmount,
    },
    {
      title: "GST Rate",
      dataIndex: "gstRate",
      key: "gstRate",
      width: 90,
      align: "center",
      render: formatGstRate,
    },
    {
      title: "SGST",
      dataIndex: "sgstAmount",
      key: "sgstAmount",
      width: 110,
      align: "right",
      render: formatAmount,
    },
    {
      title: "CGST",
      dataIndex: "cgstAmount",
      key: "cgstAmount",
      width: 110,
      align: "right",
      render: formatAmount,
    },
    {
      title: "IGST",
      dataIndex: "igstAmount",
      key: "igstAmount",
      width: 110,
      align: "right",
      render: formatAmount,
    },
    {
      title: "Round Off",
      dataIndex: "roundOff",
      key: "roundOff",
      width: 100,
      align: "right",
      render: (v) => formatAmount(Number(v) || 0),
    },
    {
      title: "Total Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 120,
      align: "right",
      render: formatAmount,
    },
    {
      title: "Against Bill No.",
      dataIndex: "againstBillNo",
      key: "againstBillNo",
      width: 140,
      align: "center",
    },
    {
      title: "Bill Date",
      dataIndex: "billDate",
      key: "billDate",
      width: 110,
      align: "center",
      render: (v) => v || "-",
    },
    {
      title: "Bill Total Amount",
      dataIndex: "billTotalAmount",
      key: "billTotalAmount",
      width: 130,
      align: "right",
      render: formatAmount,
    },
  ];

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
          }}
        />
      ),
    },
  };

  const sheetNamesInOrder = reportData?.sheetNamesInOrder ?? [];
  const dataByMonth = reportData?.dataByMonth ?? {};

  const tabItems = sheetNamesInOrder.map((sheetName) => {
    const rows = Array.isArray(dataByMonth[sheetName]) ? dataByMonth[sheetName] : [];

    const detailedTotals = rows.reduce(
      (acc, r) => {
        acc.amount += Number(r?.amount) || 0;
        acc.sgstAmount += Number(r?.sgstAmount) || 0;
        acc.cgstAmount += Number(r?.cgstAmount) || 0;
        acc.igstAmount += Number(r?.igstAmount) || 0;
        acc.totalAmount += Number(r?.totalAmount) || 0;
        acc.billTotalAmount += Number(r?.billTotalAmount) || 0;
        return acc;
      },
      {
        amount: 0,
        sgstAmount: 0,
        cgstAmount: 0,
        igstAmount: 0,
        totalAmount: 0,
        billTotalAmount: 0,
      }
    );

    return {
      key: sheetName,
      label: sheetName,
      children: (
        <Table
          columns={columns}
          dataSource={rows}
          rowKey={(r, i) => r.cnNumber?.toString() || `row-${sheetName}-${i}`}
          pagination={false}
          scroll={{ x: "max-content", y: 500 }}
          components={components}
          sticky
          size="small"
          summary={() =>
            rows.length ? (
              <Table.Summary>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={5} align="center">
                    <strong>Grand Total</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    <strong>{formatAmount(detailedTotals.amount)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} />
                  <Table.Summary.Cell index={7} align="right">
                    <strong>{formatAmount(detailedTotals.sgstAmount)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align="right">
                    <strong>{formatAmount(detailedTotals.cgstAmount)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9} align="right">
                    <strong>{formatAmount(detailedTotals.igstAmount)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={10} />
                  <Table.Summary.Cell index={11} align="right">
                    <strong>{formatAmount(detailedTotals.totalAmount)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={12} align="center" />
                  <Table.Summary.Cell index={13} align="center" />
                  <Table.Summary.Cell index={14} align="right">
                    <strong>{formatAmount(detailedTotals.billTotalAmount)}</strong>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            ) : null
          }
        />
      ),
    };
  });

  return (
    <div
      className="parent-padding-container"
      style={{ backgroundColor: "white", borderRadius: 12, padding: 20 }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <Space wrap>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              <span>Credit Note Register</span>
              {reportData?.startDate && reportData?.endDate && (
                <span style={{ color: "#666", fontSize: 13 }}>
                  ({reportData.startDate} to {reportData.endDate})
                </span>
              )}
            </Space>

            <Space wrap>
              <Switch
                checkedChildren="All months"
                unCheckedChildren="One month"
                checked={allMonths}
                onChange={setAllMonths}
              />
              <Select
                value={financialYear}
                onChange={setFinancialYear}
                style={{ minWidth: 120 }}
                placeholder="Financial Year"
              >
                {getFinancialYearOptions().map((fy) => (
                  <Option key={fy} value={fy}>
                    {fy}
                  </Option>
                ))}
              </Select>
              <Button
                type="primary"
                icon={<FaDownload />}
                loading={downloading}
                onClick={handleDownloadExcel}
                disabled={!reportData}
              >
                Download Excel
              </Button>
            </Space>
          </div>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin />
          </div>
        ) : !reportData ? (
          <div style={{ textAlign: "center", padding: 60, color: "#999" }}>
            No data found
          </div>
        ) : tabItems.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "#999" }}>
            No months in report
          </div>
        ) : (
          <Tabs
            type="card"
            activeKey={
              activeTabKey && tabItems.some((t) => t.key === activeTabKey)
                ? activeTabKey
                : tabItems[0]?.key
            }
            onChange={setActiveTabKey}
            items={tabItems}
          />
        )}
      </Card>
    </div>
  );
};

export default CreditNoteRegisterReport;
